import { useState, useRef, useEffect } from 'react'
import { formatearPeso } from '../../utils/formato'

const SWIPE_THRESHOLD = -72

function formatearCantidadKg(kg) {
  if (!kg) return '0g'
  return kg < 1 ? `${Math.round(kg * 1000)}g` : `${parseFloat(kg.toFixed(3))}kg`
}

export default function Carrito({ items, total, onQuitar, onAgregar, onEliminar, onCobrar, onCambiarPeso, onVaciar, sinBorde }) {
  const [seleccionMode, setSeleccionMode] = useState(false)
  const [seleccionados, setSeleccionados] = useState(new Set())
  const [confirmVaciar, setConfirmVaciar] = useState(false)
  const vaciarTimer = useRef(null)

  useEffect(() => () => clearTimeout(vaciarTimer.current), [])

  function handleVaciar() {
    if (!confirmVaciar) {
      setConfirmVaciar(true)
      vaciarTimer.current = setTimeout(() => setConfirmVaciar(false), 2500)
    } else {
      clearTimeout(vaciarTimer.current)
      setConfirmVaciar(false)
      onVaciar()
    }
  }

  function toggleSeleccion(id) {
    setSeleccionados(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function salirSeleccion() {
    setSeleccionMode(false)
    setSeleccionados(new Set())
  }

  function eliminarSeleccionados() {
    seleccionados.forEach(id => onEliminar(id))
    salirSeleccion()
  }

  function entrarSeleccion(id) {
    setSeleccionMode(true)
    setSeleccionados(new Set([id]))
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-tienda-muted p-6 gap-3">
        <svg className="w-16 h-16 opacity-30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
          <line x1="3" y1="6" x2="21" y2="6" />
          <path d="M16 10a4 4 0 0 1-8 0" />
        </svg>
        <p className="text-lg font-medium">Carrito vacío</p>
        <p className="text-sm text-center">Selecciona productos de la lista</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">

      {/* Barra de selección múltiple */}
      {seleccionMode && (
        <div className="flex items-center justify-between px-4 py-2.5 bg-tienda-surface border-b border-tienda-border flex-shrink-0">
          <button onClick={salirSeleccion} className="text-tienda-muted text-sm font-semibold">
            Cancelar
          </button>
          <span className="text-tienda-text text-sm font-semibold">
            {seleccionados.size} seleccionado{seleccionados.size !== 1 ? 's' : ''}
          </span>
          <button
            onClick={eliminarSeleccionados}
            disabled={seleccionados.size === 0}
            className="text-tienda-danger text-sm font-bold disabled:opacity-30"
          >
            Eliminar
          </button>
        </div>
      )}

      {/* Items */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {items.map(item => (
          <ItemCarrito
            key={item._id}
            item={item}
            onQuitar={onQuitar}
            onAgregar={onAgregar}
            onEliminar={onEliminar}
            onCambiarPeso={onCambiarPeso}
            seleccionMode={seleccionMode}
            seleccionado={seleccionados.has(item._id)}
            onToggleSeleccion={() => toggleSeleccion(item._id)}
            onLongPress={() => entrarSeleccion(item._id)}
          />
        ))}
      </div>

      {/* Total + Cobrar */}
      <div className={`p-4 bg-tienda-surface flex-shrink-0 ${sinBorde ? '' : 'border-t border-tienda-border'}`}>
        <div className="flex justify-between items-center mb-4">
          <span className="text-tienda-muted text-lg font-medium">Total</span>
          <div className="flex items-center gap-3">
            {onVaciar && !seleccionMode && (
              <button
                onClick={handleVaciar}
                className={`text-sm font-semibold transition-colors ${confirmVaciar ? 'text-tienda-danger' : 'text-tienda-muted'}`}
              >
                {confirmVaciar ? '¿Vaciar todo?' : 'Vaciar'}
              </button>
            )}
            <span className="text-3xl font-bold text-tienda-accent">{formatearPeso(total)}</span>
          </div>
        </div>
        <button
          onClick={onCobrar}
          disabled={seleccionMode}
          className="btn-primary w-full text-center disabled:opacity-40"
        >
          Cobrar {formatearPeso(total)}
        </button>
      </div>
    </div>
  )
}

function ItemCarrito({ item, onQuitar, onAgregar, onEliminar, onCambiarPeso, seleccionMode, seleccionado, onToggleSeleccion, onLongPress }) {
  const esPeso = item.unidad === 'kg'
  const [offset, setOffset] = useState(0)
  const [abierto, setAbierto] = useState(false)
  const startXRef = useRef(0)
  const startYRef = useRef(0)
  const moviendoRef = useRef(false)
  const longPressTimer = useRef(null)

  function onTouchStart(e) {
    startXRef.current = e.touches[0].clientX
    startYRef.current = e.touches[0].clientY
    moviendoRef.current = false
    if (!seleccionMode) {
      longPressTimer.current = setTimeout(() => {
        onLongPress()
      }, 500)
    }
  }

  function onTouchMove(e) {
    clearTimeout(longPressTimer.current)
    if (seleccionMode) return
    const dx = e.touches[0].clientX - startXRef.current
    const dy = e.touches[0].clientY - startYRef.current
    if (!moviendoRef.current && Math.abs(dy) > Math.abs(dx)) return
    moviendoRef.current = true
    e.preventDefault()
    const base = abierto ? SWIPE_THRESHOLD : 0
    const next = Math.min(0, Math.max(-90, base + dx))
    setOffset(next)
  }

  function onTouchEnd() {
    clearTimeout(longPressTimer.current)
    if (!moviendoRef.current) {
      if (seleccionMode) onToggleSeleccion()
      return
    }
    if (offset < SWIPE_THRESHOLD / 2) {
      setOffset(SWIPE_THRESHOLD)
      setAbierto(true)
    } else {
      setOffset(0)
      setAbierto(false)
    }
  }

  function cerrar() {
    setOffset(0)
    setAbierto(false)
  }

  return (
    <div className="relative overflow-hidden rounded-2xl">
      {/* Fondo rojo */}
      <div className="absolute inset-0 flex items-center justify-end bg-tienda-danger rounded-2xl">
        <button
          onClick={() => { cerrar(); onEliminar(item._id) }}
          className="w-20 h-full flex flex-col items-center justify-center gap-1 text-white active:brightness-90"
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6M14 11v6" />
            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
          </svg>
          <span className="text-xs font-bold">Quitar</span>
        </button>
      </div>

      {/* Tarjeta deslizable */}
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          transform: `translateX(${offset}px)`,
          transition: moviendoRef.current ? 'none' : 'transform 0.2s ease',
        }}
        className={`card p-3 flex items-center gap-2 relative z-10
          ${seleccionado ? 'ring-2 ring-tienda-accent' : ''}
          ${seleccionMode ? 'cursor-pointer' : ''}`}
      >
        {/* Checkbox modo selección */}
        {seleccionMode && (
          <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center
            ${seleccionado ? 'bg-tienda-accent border-tienda-accent' : 'border-tienda-border'}`}>
            {seleccionado && (
              <svg className="w-3 h-3 text-gray-900" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            )}
          </div>
        )}

        <div className="w-9 h-9 rounded-lg bg-tienda-bg flex items-center justify-center text-xl flex-shrink-0">
          {item.emoji || item.nombre.charAt(0)}
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-tienda-text truncate">{item.nombre}</p>
          <p className="text-tienda-muted text-xs">
            {formatearPeso(item.precio)} {esPeso ? '/kg' : 'c/u'}
          </p>
        </div>

        {!seleccionMode && (
          <>
            {esPeso ? (
              /* Producto por peso: mostrar cantidad + botón editar peso */
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="font-bold text-tienda-text text-sm bg-tienda-bg border border-tienda-border px-2 py-1 rounded-lg">
                  {formatearCantidadKg(item.cantidad)}
                </span>
                {onCambiarPeso && (
                  <button
                    onClick={() => onCambiarPeso(item)}
                    className="w-8 h-8 rounded-lg bg-tienda-bg border border-tienda-border text-tienda-muted flex items-center justify-center active:scale-90"
                    title="Cambiar peso"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                )}
              </div>
            ) : (
              /* Producto por pieza: botones −/cantidad/+ */
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => onQuitar(item._id)}
                  className="w-8 h-8 rounded-lg bg-tienda-bg border border-tienda-border text-tienda-text font-bold text-lg flex items-center justify-center active:scale-90"
                >
                  −
                </button>
                <span className="w-7 text-center font-bold text-tienda-text">{item.cantidad}</span>
                <button
                  onClick={() => onAgregar(item)}
                  className="w-8 h-8 rounded-lg bg-tienda-bg border border-tienda-border text-tienda-text font-bold text-lg flex items-center justify-center active:scale-90"
                >
                  +
                </button>
              </div>
            )}
            <p className="text-tienda-accent font-bold text-base flex-shrink-0 ml-1">
              {formatearPeso(item.precioFinal != null ? item.precioFinal : item.precio * item.cantidad)}
            </p>
          </>
        )}

        {seleccionMode && (
          <p className="text-tienda-accent font-bold text-base flex-shrink-0">
            {formatearPeso(item.precioFinal != null ? item.precioFinal : item.precio * item.cantidad)}
          </p>
        )}
      </div>
    </div>
  )
}
